import { Table, Space, Button, Tag, Tooltip } from "antd";
import {
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface VarietyBreakdown {
  id: number | null;
  code: string;
  name: string;
  // PR4 — Σ Plot.surface por variedad (declarada).
  licensed_surface: number;
  // PR4 — Σ PlotReal.surface por variedad (auditada).
  audited_surface: number;
  // [legacy] alias de licensed_surface.
  surface?: number;
}

interface Farm {
  id: number;
  name: string;
  province_name: string;
  town_name: string;
  // [DEPRECATED] PR4: `surface_tango` manual ya no se muestra; la
  // UI lee `licensed_surface_total` derivado de las Plot.
  surface_tango: number;
  audited_surface?: number;
  is_active?: boolean;
  // [DEPRECATED] PR3: lo dejamos como fallback hasta PR7 para fincas
  // cuyo backend no haya migrado todavía.
  variety_display?: string;
  // PR3 — Desglose por variedad calculado en el backend.
  varieties?: VarietyBreakdown[];
  // PR4 — Totales derivados del desglose.
  licensed_surface_total?: number;
  audited_surface_total?: number;
  surface_diff_total?: number;
  needs_full_reaudit?: boolean;
  [key: string]: any;
}

interface FarmTableProps {
  farms: Farm[];
  loading?: boolean;
  onEdit?: (farm: Farm) => void;
  onDeactivate?: (farmId: number) => void;
  onActivate?: (farmId: number) => void;
  // Toggle the "Necesita reauditoría" flag — passes the next value
  // so the parent knows what state to write.
  onToggleReaudit?: (farmId: number, nextValue: boolean) => void;
}

export const FarmTable = ({ farms, loading, onEdit, onDeactivate, onActivate, onToggleReaudit }: FarmTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Table dataSource={farms} rowKey="id" loading={loading} pagination={false} scroll={{ x: 900 }}>
      <Table.Column
        dataIndex="name"
        title={t("producers.farms.fields.name", "Nombre")}
        width={200}
        sorter={(a: Farm, b: Farm) => a.name.localeCompare(b.name)}
      />
      <Table.Column
        title={t("producers.farms.fields.location", "Municipio (Provincia)")}
        width={220}
        sorter={(a: Farm, b: Farm) => {
          const locA = `${a.town_name || ""} ${a.province_name || ""}`;
          const locB = `${b.town_name || ""} ${b.province_name || ""}`;
          return locA.localeCompare(locB);
        }}
        render={(_: unknown, record: Farm) => {
          const town = record.town_name || "";
          const province = record.province_name || "";
          if (town && province) return `${town} (${province})`;
          return town || province || "-";
        }}
      />
      <Table.Column
        title={t("producers.farms.fields.variety", "Variedad")}
        width={220}
        sorter={(a: Farm, b: Farm) => {
          const labelA = (a.varieties?.map((v) => v.name).join(", ")) || a.variety_display || "";
          const labelB = (b.varieties?.map((v) => v.name).join(", ")) || b.variety_display || "";
          return labelA.localeCompare(labelB);
        }}
        // PR4 — Chips por variedad con comparación licenciada vs
        // auditada en el tooltip. Si el backend aún no devuelve
        // `varieties` (versión antigua), caemos al `variety_display`.
        render={(_: unknown, record: Farm) => {
          const breakdown = record.varieties ?? [];
          if (breakdown.length === 0) {
            return record.variety_display || "-";
          }
          return (
            <Space size={4} wrap>
              {breakdown.map((v) => {
                const licensed = v.licensed_surface ?? v.surface ?? 0;
                const audited = v.audited_surface ?? 0;
                const diff = audited - licensed;
                const sign = diff === 0 ? "" : diff > 0 ? "+" : "";
                return (
                  <Tooltip
                    key={v.code}
                    title={
                      <div>
                        <div><strong>{v.name}</strong></div>
                        <div>Licenciada: {licensed.toFixed(2)} ha</div>
                        <div>Auditada: {audited.toFixed(2)} ha</div>
                        <div>
                          Diferencia: {sign}
                          {diff.toFixed(2)} ha
                        </div>
                      </div>
                    }
                  >
                    <Tag color="blue" style={{ margin: 0 }}>
                      {v.name}
                    </Tag>
                  </Tooltip>
                );
              })}
            </Space>
          );
        }}
      />
      <Table.Column
        title={t("producers.farms.fields.surface_licensed", "Sup. Licenciada")}
        width={150}
        align="right"
        sorter={(a: Farm, b: Farm) =>
          (a.licensed_surface_total ?? 0) - (b.licensed_surface_total ?? 0)
        }
        render={(_: unknown, record: Farm) => {
          const total = record.licensed_surface_total ?? 0;
          const breakdown = record.varieties ?? [];
          const hasMulti = breakdown.length > 1;
          const cell = <span style={{ fontWeight: 500 }}>{total.toFixed(2)}</span>;
          if (!hasMulti) return cell;
          return (
            <Tooltip
              title={
                <div>
                  {breakdown.map((v) => (
                    <div key={v.code}>
                      {v.name}: {(v.licensed_surface ?? 0).toFixed(2)} ha
                    </div>
                  ))}
                  <div style={{ borderTop: "1px solid #555", marginTop: 4, paddingTop: 4 }}>
                    <strong>Total: {total.toFixed(2)} ha</strong>
                  </div>
                </div>
              }
            >
              {cell}
            </Tooltip>
          );
        }}
      />
      <Table.Column
        title={t("producers.farms.fields.audited_surface", "Sup. Auditada")}
        width={150}
        align="right"
        sorter={(a: Farm, b: Farm) =>
          (a.audited_surface_total ?? 0) - (b.audited_surface_total ?? 0)
        }
        render={(_: unknown, record: Farm) => {
          const total = record.audited_surface_total ?? 0;
          const breakdown = record.varieties ?? [];
          const hasMulti = breakdown.length > 1;
          const cell = <span style={{ fontWeight: 500 }}>{total.toFixed(2)}</span>;
          if (!hasMulti) return cell;
          return (
            <Tooltip
              title={
                <div>
                  {breakdown.map((v) => (
                    <div key={v.code}>
                      {v.name}: {(v.audited_surface ?? 0).toFixed(2)} ha
                    </div>
                  ))}
                  <div style={{ borderTop: "1px solid #555", marginTop: 4, paddingTop: 4 }}>
                    <strong>Total: {total.toFixed(2)} ha</strong>
                  </div>
                </div>
              }
            >
              {cell}
            </Tooltip>
          );
        }}
      />
      <Table.Column
        title={t("producers.farms.fields.status", "Estado")}
        width={140}
        dataIndex="is_active"
        sorter={(a: Farm, b: Farm) => Number(a.is_active ?? true) - Number(b.is_active ?? true)}
        render={(value: boolean | undefined, record: Farm) => (
          <Space size={4} wrap>
            {value === false ? (
              <Tag color="red">{t("common.inactive", "Inactiva")}</Tag>
            ) : (
              <Tag color="green">{t("common.active", "Activa")}</Tag>
            )}
            {record.needs_full_reaudit && (
              <Tooltip
                title={t(
                  "producers.farms.needs_reaudit_tooltip",
                  "Marcada para auditoría completa en la próxima planificación.",
                )}
              >
                <Tag color="gold" icon={<ThunderboltFilled />}>
                  {t("producers.farms.needs_reaudit_short", "Reauditar")}
                </Tag>
              </Tooltip>
            )}
          </Space>
        )}
      />
      <Table.Column
        title={t("common.actions", "Acciones")}
        width={180}
        fixed="right"
        render={(_: unknown, record: Farm) => (
          <Space>
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/farms/show/${record.id}`, { state: { from: "producer" } })} title={t("common.show", "Ver detalle")} />
            {onEdit && (
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
            )}
            {onToggleReaudit && (
              <Tooltip
                title={
                  record.needs_full_reaudit
                    ? t(
                        "producers.farms.actions.clear_reaudit",
                        "Marcada para reauditoría. Pulsa para quitar la marca.",
                      )
                    : t(
                        "producers.farms.actions.mark_reaudit",
                        "Marcar para auditoría completa (parcelas nuevas)",
                      )
                }
              >
                {/* When marked, switch to a primary-style filled
                    button with a warm background + bold icon — that
                    way the row visibly shows that the action has
                    taken effect, even outside the Estado tag. */}
                <Button
                  type={record.needs_full_reaudit ? "primary" : "text"}
                  size="small"
                  style={
                    record.needs_full_reaudit
                      ? {
                          backgroundColor: "#faad14",
                          borderColor: "#faad14",
                          color: "#fff",
                          boxShadow: "0 0 0 2px rgba(250, 173, 20, 0.2)",
                        }
                      : undefined
                  }
                  icon={
                    record.needs_full_reaudit ? (
                      <ThunderboltFilled />
                    ) : (
                      <ThunderboltOutlined />
                    )
                  }
                  onClick={() =>
                    onToggleReaudit(record.id, !record.needs_full_reaudit)
                  }
                />
              </Tooltip>
            )}
            {record.is_active !== false && onDeactivate && (
              <Button type="text" size="small" icon={<StopOutlined />} onClick={() => onDeactivate(record.id)} title={t("producers.farms.actions.deactivate", "Desactivar")} />
            )}
            {record.is_active === false && onActivate && (
              <Button type="text" size="small" style={{ color: "#52c41a" }} icon={<CheckCircleOutlined />} onClick={() => onActivate(record.id)} title={t("producers.farms.actions.activate", "Activar")} />
            )}
          </Space>
        )}
      />
    </Table>
  );
};
